import { downloadAuthed } from './downloadAuthed';
import { pathWithQuery } from './download';

/**
 * Authenticated CSV download helper.
 *
 * The CSV callers' original signature (path + query + fallback name), now
 * a thin wrapper over the shared downloadAuthed (lib/downloadAuthed.ts,
 * rules in lib/download.ts, 29 Oct 2026), so every Admin download takes
 * one path: the signed-in ky client (Bearer attached, 401 refresh and one
 * replay), the filename from Content-Disposition, an object-URL save, and
 * one download per double click.
 *
 * Why this exists
 * ───────────────
 * The auth plugin at apps/api/src/plugins/auth.ts reads only the
 * `Authorization: Bearer` header. Top-level browser navigation via
 * `<a href download>` carries cookies but cannot attach a JS-managed
 * Bearer token, so the previous `csvHref` pattern 401'd every download.
 *
 * Loading state + error handling are the CALLER's responsibility. A
 * failure throws a DownloadError whose message says why (not signed in,
 * no permission, not found, server error, network), ready to show as is;
 * it keeps the Api's response so getDisplayErrorMessage still reads a
 * VALIDATION_ERROR message.
 *
 * Path semantics
 * ──────────────
 * `path` is a KY-relative path (no leading slash, no BASE_URL prefix).
 * `query` values that are `undefined` / `null` / `''` are dropped, so
 * callers can spread conditional filter params freely.
 *
 * Example:
 *   await downloadAuthenticatedCsv({
 *     path: 'sessions/export',
 *     query: { location_id: locationId ?? undefined, from, to },
 *     fallbackFilename: `evi-sessions-${today}.csv`,
 *   });
 */
export async function downloadAuthenticatedCsv(opts: {
  /** KY-relative path. No leading slash, no BASE_URL prefix. */
  path: string;
  /** Query params. Undefined / null / empty-string values dropped. */
  query?: Record<string, string | undefined | null>;
  /** Filename used when the server omits a Content-Disposition
   *  header or the header can't be parsed. Include the .csv suffix
   *  and a date stamp for predictability. */
  fallbackFilename: string;
}): Promise<void> {
  await downloadAuthed(pathWithQuery(opts.path, opts.query), opts.fallbackFilename);
}
